import { useCallback, useEffect, useState } from "react";
import { AttributeType, StringSubType } from "@evergis/api";

export type UpdateAttribute<Attribute> = (id: string, attribute: Partial<Attribute>) => void;

export interface AttributesUpdate<Attribute> {
  updateAttribute: UpdateAttribute<Attribute>;
  addAttribute: (attribute: Attribute) => void;
  deleteAttribute: (id: string) => void;
  setAttributes: (state: Attribute[]) => void;
}

// TODO: refactor that, generic, magic "id" field which uses for all attributes
export const useAttributes = <Attribute>(attributes: Attribute[]): [Attribute[], AttributesUpdate<Attribute>] => {
  const [attributesState, setAttributes] = useState(attributes);

  const updateAttribute = useCallback(
    (id: string, attribute: Partial<Attribute>) => setAttributes(update(id, attribute)),
    [setAttributes],
  );

  const deleteAttribute = useCallback(id => setAttributes(remove(id)), [setAttributes]);

  const addAttribute = useCallback((attribute: Attribute) => setAttributes(add(attribute)), [setAttributes]);

  useEffect(() => {
    setAttributes(attributes);
  }, [attributes]);

  return [attributesState, { updateAttribute, setAttributes, addAttribute, deleteAttribute }];
};

function update<Attribute extends { id?: string; type?: string }>(id: string, attributeUpdate: Attribute) {
  return <A extends Attribute>(attributes: A[]) => {
    return attributes?.map(attribute => {
      if (attribute.id !== id) return attribute;

      let modifiedAttribute;

      if (attributeUpdate.type === StringSubType.Image) {
        modifiedAttribute = { ...attributeUpdate, type: AttributeType.String, subType: StringSubType.Image };
      } else if (attributeUpdate.type === StringSubType.Attachments) {
        modifiedAttribute = { ...attributeUpdate, type: AttributeType.Json, subType: StringSubType.Attachments };
      } else if (!attributeUpdate.type) {
        modifiedAttribute = { ...attributeUpdate };
      } else {
        modifiedAttribute = { ...attributeUpdate, subType: undefined };
      }

      return { ...attribute, ...modifiedAttribute };
    });
  };
}

function remove(id: string) {
  return <Attribute extends { id?: string }>(attributes: Attribute[]) =>
    attributes?.filter(attribute => attribute.id !== id);
}

function add<Attribute extends { id?: string }>(attribute: Attribute) {
  return (attributes: Attribute[]) => (attributes ? [...attributes, attribute] : [attribute]);
}
